import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const ROLES = ["user", "dealer", "admin"];

export default function UsersTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [verified, setVerified] = useState("");
  const [suspended, setSuspended] = useState("");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // user_id

  const load = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (q) params.q = q;
    if (role) params.role = role;
    if (verified) params.verified = verified;
    if (suspended) params.suspended = suspended;
    api.get("/admin/users", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [role, verified, suspended]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [q]);

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="flex flex-wrap items-center gap-2 border border-border p-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…" className="rounded-none max-w-xs" data-testid="users-search" />
        <Select value={role} onValueChange={(v) => setRole(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-none w-40" data-testid="users-role-filter"><SelectValue placeholder="Any role" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="__all__">Any role</SelectItem>
            {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={verified} onValueChange={(v) => setVerified(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-none w-40" data-testid="users-verified-filter"><SelectValue placeholder="Any verification" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="__all__">Any verification</SelectItem>
            <SelectItem value="true">Verified</SelectItem>
            <SelectItem value="false">Unverified</SelectItem>
          </SelectContent>
        </Select>
        <Select value={suspended} onValueChange={(v) => setSuspended(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-none w-40" data-testid="users-suspended-filter"><SelectValue placeholder="Any status" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="__all__">Any status</SelectItem>
            <SelectItem value="false">Active</SelectItem>
            <SelectItem value="true">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-none uppercase text-xs h-9 ml-auto" onClick={load} data-testid="users-refresh">Refresh</Button>
      </div>

      <div className="border border-border overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-muted/50 text-left">
            <tr>
              <Th>User</Th><Th>Role</Th><Th>Verified</Th><Th>Status</Th><Th>Created</Th><Th>Last Login</Th><Th>Listings</Th><Th>Deals</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No users found.</td></tr>}
            {items.map((u) => (
              <tr key={u.user_id} className="border-t border-border" data-testid={`users-row-${u.user_id}`}>
                <Td>
                  <div className="text-[12px]">{u.name || "—"}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{u.email}</div>
                </Td>
                <Td><span className="text-[10px] font-mono uppercase">{u.role || "user"}</span></Td>
                <Td>{u.email_verified ? <Yes /> : <No />}</Td>
                <Td>{u.suspended ? <span className="text-[10px] font-mono uppercase text-red-600">Suspended</span> : <span className="text-[10px] font-mono uppercase text-emerald-600">Active</span>}</Td>
                <Td className="text-[10px] font-mono">{u.created_at?.slice(0, 10) || "—"}</Td>
                <Td className="text-[10px] font-mono">{u.last_login_at?.slice(0, 10) || u.last_seen_at?.slice(0, 10) || "—"}</Td>
                <Td className="font-mono text-[11px]">{u.stats?.listings ?? 0}</Td>
                <Td className="font-mono text-[11px]">{(u.stats?.deals_as_buyer ?? 0) + (u.stats?.deals_as_seller ?? 0)}</Td>
                <Td>
                  <Button size="sm" variant="outline" className="rounded-none text-[10px] h-7 uppercase" data-testid={`users-manage-${u.user_id}`} onClick={() => setDetail(u.user_id)}>
                    Manage
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && <UserDrawer userId={detail} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-overline text-muted-foreground text-[10px] whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;
const Yes = () => <span className="text-[10px] font-mono uppercase text-emerald-600">Yes</span>;
const No  = () => <span className="text-[10px] font-mono uppercase text-muted-foreground">No</span>;

function UserDrawer({ userId, onClose }) {
  const [u, setU] = useState(null);
  const [note, setNote] = useState("");
  const [suspendReason, setSuspendReason] = useState("");
  const [showSuspend, setShowSuspend] = useState(false);
  const [showRole, setShowRole] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [listings, setListings] = useState([]);
  const [deals, setDeals] = useState([]);

  const load = () => {
    api.get(`/admin/users/${userId}`).then((r) => setU(r.data)).catch(() => setU(null));
    api.get(`/admin/users/${userId}/listings`).then((r) => setListings(r.data.items || [])).catch(() => setListings([]));
    api.get(`/admin/users/${userId}/deals`).then((r) => setDeals(r.data.items || [])).catch(() => setDeals([]));
  };
  useEffect(load, [userId]);

  const call = async (fn, ok = "Done") => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const suspend = () =>
    call(async () => {
      if (!suspendReason.trim()) throw { response: { data: { detail: "Reason required" } } };
      await api.post(`/admin/users/${userId}/suspend`, { reason: suspendReason.trim() });
      setShowSuspend(false); setSuspendReason("");
    }, "User suspended");

  const reactivate = () => call(() => api.post(`/admin/users/${userId}/reactivate`), "User reactivated");
  const verify     = () => call(() => api.post(`/admin/users/${userId}/verify`),     "User verified");
  const unverify   = () => call(() => api.post(`/admin/users/${userId}/unverify`),   "Verification cleared");

  const changeRole = () =>
    call(async () => {
      if (!newRole) throw { response: { data: { detail: "Select a role" } } };
      await api.post(`/admin/users/${userId}/role`, { role: newRole });
      setShowRole(false); setNewRole("");
    }, "Role updated");

  const addNote = () =>
    call(async () => {
      if (!note.trim()) throw { response: { data: { detail: "Note is empty" } } };
      await api.post(`/admin/users/${userId}/note`, { text: note.trim() });
      setNote("");
    }, "Note added");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-drawer">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {u?.name || "User"} <span className="text-sm text-muted-foreground font-mono">{u?.email}</span>
          </DialogTitle>
        </DialogHeader>

        {!u ? <div className="text-muted-foreground p-4">Loading…</div> : (
          <div className="space-y-5 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border border-border p-3">
              <Meta k="Role" v={u.role || "user"} />
              <Meta k="Verified" v={u.email_verified ? "Yes" : "No"} />
              <Meta k="Status" v={u.suspended ? "Suspended" : "Active"} />
              <Meta k="Created" v={u.created_at?.slice(0, 10) || "—"} />
              <Meta k="Listings" v={u.stats?.listings ?? 0} />
              <Meta k="Offers" v={u.stats?.offers ?? 0} />
              <Meta k="Buyer Deals" v={u.stats?.deals_as_buyer ?? 0} />
              <Meta k="Seller Deals" v={u.stats?.deals_as_seller ?? 0} />
            </div>

            <div className="flex flex-wrap gap-2">
              {u.suspended ? (
                <Button variant="outline" className="rounded-none uppercase text-xs h-9" disabled={busy} onClick={reactivate} data-testid="user-action-reactivate">Reactivate</Button>
              ) : (
                <Button variant="outline" className="rounded-none uppercase text-xs h-9 text-red-600" disabled={busy} onClick={() => setShowSuspend(true)} data-testid="user-action-suspend">Suspend</Button>
              )}
              {u.email_verified ? (
                <Button variant="outline" className="rounded-none uppercase text-xs h-9" disabled={busy} onClick={unverify} data-testid="user-action-unverify">Unverify</Button>
              ) : (
                <Button variant="outline" className="rounded-none uppercase text-xs h-9" disabled={busy} onClick={verify} data-testid="user-action-verify">Verify</Button>
              )}
              <Button variant="outline" className="rounded-none uppercase text-xs h-9" disabled={busy} onClick={() => { setNewRole(u.role || "user"); setShowRole(true); }} data-testid="user-action-role">Change Role</Button>
            </div>

            <div className="border border-border p-4">
              <div className="font-overline text-accent mb-2">Add Internal Note</div>
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Only visible to admins…" className="rounded-none" data-testid="user-note-input" />
              <div className="flex justify-end mt-2">
                <Button className="rounded-none uppercase text-xs h-9" disabled={busy || !note.trim()} onClick={addNote} data-testid="user-note-save">Save Note</Button>
              </div>
              {(u.internal_notes || []).length > 0 && (
                <div className="mt-4 space-y-2 max-h-32 overflow-y-auto">
                  {u.internal_notes.slice().reverse().map((n) => (
                    <div key={n.note_id} className="text-xs border-l-2 border-primary pl-3 py-1">
                      <div className="text-muted-foreground font-mono text-[10px]">{n.at ? new Date(n.at).toLocaleString() : ""}</div>
                      <div>{n.text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-border p-3">
                <div className="font-overline text-accent mb-2">Listings ({listings.length})</div>
                <div className="max-h-48 overflow-y-auto text-xs" data-testid="user-listings">
                  {listings.length === 0 && <div className="text-muted-foreground">No listings.</div>}
                  {listings.map((l) => (
                    <Link key={l.note_id} to={`/notes/${l.note_id}`} className="flex justify-between border-b border-border/50 py-1 hover:bg-muted/30">
                      <span>{l.vehicle_year} {l.vehicle_make} {l.vehicle_model}</span>
                      <span className="text-muted-foreground font-mono text-[10px]">{l.listing_status || (l.listed ? "listed" : "hidden")}</span>
                    </Link>
                  ))}
                </div>
              </div>
              <div className="border border-border p-3">
                <div className="font-overline text-accent mb-2">Deals ({deals.length})</div>
                <div className="max-h-48 overflow-y-auto text-xs" data-testid="user-deals">
                  {deals.length === 0 && <div className="text-muted-foreground">No deals.</div>}
                  {deals.map((d) => (
                    <Link key={d.deal_id} to={`/deals/${d.deal_id}`} className="flex justify-between border-b border-border/50 py-1 hover:bg-muted/30">
                      <span>{d.deal_id.slice(-10)}</span>
                      <span className="text-muted-foreground font-mono text-[10px]">{d.status}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="user-drawer-close">Close</Button>
        </DialogFooter>
      </DialogContent>

      {showSuspend && (
        <Dialog open onOpenChange={setShowSuspend}>
          <DialogContent className="rounded-none max-w-md" data-testid="user-suspend-dialog">
            <DialogHeader><DialogTitle>Suspend user</DialogTitle></DialogHeader>
            <Textarea value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} placeholder="Reason (required)" className="rounded-none" data-testid="user-suspend-reason" />
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setShowSuspend(false)}>Cancel</Button>
              <Button className="rounded-none" disabled={busy} onClick={suspend} data-testid="user-suspend-confirm">Suspend</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {showRole && (
        <Dialog open onOpenChange={setShowRole}>
          <DialogContent className="rounded-none max-w-md" data-testid="user-role-dialog">
            <DialogHeader><DialogTitle>Change role</DialogTitle></DialogHeader>
            <div className="space-y-2 text-sm">
              <div className="text-xs text-muted-foreground">
                You cannot remove your own admin access, and the final active admin cannot be demoted.
              </div>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="rounded-none" data-testid="user-role-select"><SelectValue placeholder="Choose role" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setShowRole(false)}>Cancel</Button>
              <Button className="rounded-none" disabled={busy || !newRole} onClick={changeRole} data-testid="user-role-confirm">Apply</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

const Meta = ({ k, v }) => (
  <div>
    <div className="font-overline text-[10px] text-muted-foreground">{k}</div>
    <div className="font-mono text-[12px]">{String(v ?? "—")}</div>
  </div>
);
